import PropTypes from 'prop-types';
import React from 'react';

import { Button } from '../../../components';
import { DetailRow } from './detail-row';
import { amountByCurrency, fiatSuffix, rateDisplay } from './refund-format';

const dash = '--';
const hasValue = (value) => value !== undefined && value !== null && value !== '';
const firstValue = (...values) => values.find(hasValue);
const firstCurrency = (...values) =>
  values.find((value) => typeof value === 'string' && value.trim());

/**
 * Accept-underpayment confirmation (no refund). Shows the settlement summary
 * and the acceptance-rate warning before 2FA. Figures come from the dispute
 * record (accept has no preview); missing values render as "--".
 */
const AcceptDetailStep = ({ dispute, onContinue, onBack }) => {
  const settlementCurrency = dispute?.settlement_currency || dispute?.currency_slug;
  const expectedAmount = firstValue(
    dispute?.expected_amount,
    dispute?.payment_amount,
    dispute?.transaction_payment_amount,
    dispute?.transaction?.payment_amount
  );
  const actualPaid = firstValue(
    dispute?.actual_amount_paid,
    dispute?.actual_paid_amount,
    dispute?.amount_paid,
    dispute?.transaction_amount_paid,
    dispute?.transaction?.amount_paid
  );
  const paymentCurrency =
    firstCurrency(
      dispute?.payment_currency_slug,
      dispute?.payment_currency,
      dispute?.expected_currency,
      dispute?.currency_slug
    ) || settlementCurrency;

  return (
    <div className="resolve resolve--accept">
      <p className="resolve__subtitle">
        Settlement value is calculated using the current exchange rate at acceptance time.
      </p>

      <div className="resolve__rows resolve__rows--bordered">
        <DetailRow label="CUSTOMER ID" value={dispute?.customer_email} />
        <DetailRow
          label="EXPECTED AMOUNT"
          value={
            hasValue(expectedAmount) ? amountByCurrency(expectedAmount, paymentCurrency) : dash
          }
        />
        <DetailRow
          label="ACTUAL AMOUNT PAID"
          value={hasValue(actualPaid) ? amountByCurrency(actualPaid, paymentCurrency) : dash}
        />
        <DetailRow
          label="EXPECTED BALANCE"
          value={amountByCurrency(
            dispute?.excess_amount ?? dispute?.amount,
            dispute?.excess_currency ?? settlementCurrency
          )}
        />
        <DetailRow
          label="SETTLEMENT CURRENCY"
          info="Currency settled in your account"
          value={(settlementCurrency || '').toUpperCase() || dash}
        />
        <DetailRow
          label="CURRENT EXCHANGE RATE"
          value={
            dispute?.exchange_rate
              ? rateDisplay(paymentCurrency, dispute.exchange_rate, settlementCurrency)
              : dash
          }
        />
        <DetailRow
          label="CALCULATED SETTLEMENT VALUE"
          value={
            dispute?.calculated_settlement_value
              ? fiatSuffix(dispute.calculated_settlement_value, settlementCurrency)
              : dash
          }
        />
      </div>

      <div className="resolve__warning" role="note" data-testid="accept-warning">
        This uses the current rate, not the original payment-time rate. The acceptance-time rate
        will be stored on the record.
      </div>

      <div className="resolve__actions">
        <Button variant="outline" type="button" onClick={onBack} data-testid="accept-back-btn">
          Back
        </Button>
        <Button type="button" onClick={onContinue} data-testid="accept-continue-btn">
          Continue
        </Button>
      </div>
    </div>
  );
};

AcceptDetailStep.propTypes = {
  dispute: PropTypes.object,
  onContinue: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired
};

export { AcceptDetailStep };
